import './style.css';
import 'leaflet.polylinemeasure/Leaflet.PolylineMeasure.css'; // Import the CSS
import L from 'leaflet';
// @ts-ignore
window.L = L; // Expose L globally before importing markercluster
import 'leaflet.markercluster';

import 'leaflet/dist/leaflet.css';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;


import { initMap } from './map/mapinit';
import { createBaseLayers } from './map/layers';
import { createOverlays, loadOverlayData } from './map/overlays';
import { addLayerControl } from './map/controls';
import { updateNzChartLayers, nzChartLayerGroup } from './map/nzCharts';
import { updateUrlFromMapState, applyMapStateFromUrl, UrlState, getOverlaysFromUrl } from './map/urlState';
import { addMiscControls, addChartLayersControl } from './map/miscControls';
import { initSettingsPanel } from './ui/settingsPanel';
import { initCoordsPanel } from './ui/coordsPanel';
import { initTrainingPanel } from './ui/trainingPanel';
import { initChartLayersPanel } from './ui/chartLayersPanel';
import { initIntroPanel } from './ui/introPanel';
import { initCoastguardPanel } from './ui/coastguardPanel';
import { initPassagePlanner } from './ui/passagePlanner';
import { initPspPlanner } from './ui/searchPlanner';
import { hasSearchPatternLayers } from './map/searchPatterns';
import { encodeObjectForUrl, decodeObjectFromUrl } from './util/urlEncoding';

import type { LazyOverlay } from './map/overlays';

import { setHeadMeta } from './ui/head';

setHeadMeta(
    'SeaMap – New Zealand Marine Charts & Coastal Navigation for Education and Local Area Familiarisation',
    'Interactive Free New Zealand marine charts and other maps plus local area information tools for educational purposes.'
);

document.querySelector<HTMLDivElement>('#app')!.innerHTML = `
  <div id="map"></div>
  <input type="text" id="map-filter-input" placeholder="Filter map data..."
         title="Filter data points by name or ID. Prefix with '*' to search anywhere in the pop-up text." style="display: none;" />
`;
const { baseLayers, defaultLayer } = createBaseLayers();

const map = initMap(defaultLayer);

// Get overlay names from URL before creating them
const dynamicOverlays = getOverlaysFromUrl();
const overlays = createOverlays(dynamicOverlays);

addLayerControl(map, baseLayers, overlays);
const measureControl = addMiscControls(map); // Capture the measure control instance

const mapFilterInput = document.getElementById('map-filter-input') as HTMLInputElement;

// Apply state from URL immediately after initialization
let urlState: UrlState = applyMapStateFromUrl(map, baseLayers, overlays, mapFilterInput);


let chartUpdateDebounceTimer: ReturnType<typeof setTimeout>;
let filterInputDebounceTimer: ReturnType<typeof setTimeout>;

const DATA_POINT_LAYERS = [
    'Beaches', 'Bays', 'Islands', 'Others', 'Lights', 'Howick', 'Maraetai', 'VHF',
    'LINZ Gazetteer', 'LINZ Lights', 'Tangata Whenua', 'OSM Gazetteer', 'Extra', 'CG Units'
];

const updateFilterInputVisibility = () => {
    let hasDataLayer = false;
    for (const name of DATA_POINT_LAYERS) {
        if (overlays[name] && map.hasLayer(overlays[name])) {
            hasDataLayer = true;
            break;
        }
    }
    mapFilterInput.style.display = hasDataLayer ? 'block' : 'none';
};

const updateChartLayersControlVisibility = () => {
    const control = document.getElementById('chart-layers-control');
    if (control) {
        const chartsVisible = map.hasLayer(nzChartLayerGroup);
        const pspVisible = hasSearchPatternLayers();
        // TODO: Add check for passage plan layers when refactored
        control.style.display = (chartsVisible || pspVisible) ? 'block' : 'none';
    }
};

const reloadActiveOverlays = () => {
    const currentFilter = mapFilterInput.value;
    urlState.filter = currentFilter;

    map.eachLayer(layer => {
        if ((layer as any).overlayId && map.hasLayer(layer)) {
            loadOverlayData(layer as LazyOverlay, map, currentFilter, urlState.unit, urlState.level, urlState.mode).catch(console.error);
        }
    });

    clearTimeout(chartUpdateDebounceTimer);
    chartUpdateDebounceTimer = setTimeout(() => {
        updateNzChartLayers(map).catch(console.error);
    }, 2000);

    updateUrlFromMapState(map, baseLayers, overlays, currentFilter, urlState.unit, urlState.level, urlState.mode, urlState.searchPattern, urlState.passagePlan);
    localStorage.setItem('locatia', JSON.stringify(map.getCenter()));
};

map.on('moveend', reloadActiveOverlays);

map.on('overlayadd', async (e: L.LayersControlEvent) => {
    updateFilterInputVisibility();
    const currentFilter = mapFilterInput.value;
    const layer = e.layer as LazyOverlay;
    if (layer.overlayId) {
        await loadOverlayData(layer, map, currentFilter, urlState.unit, urlState.level, urlState.mode);
    }
    updateUrlFromMapState(map, baseLayers, overlays, currentFilter, urlState.unit, urlState.level, urlState.mode, urlState.searchPattern, urlState.passagePlan);
});

map.on('overlayremove', () => {
    updateFilterInputVisibility();
    const currentFilter = mapFilterInput.value;
    updateUrlFromMapState(map, baseLayers, overlays, currentFilter, urlState.unit, urlState.level, urlState.mode, urlState.searchPattern, urlState.passagePlan);
});

map.on('baselayerchange', (e: L.LayersControlEvent) => {
    updateChartLayersControlVisibility();
    const currentFilter = mapFilterInput.value;
    if (e.layer === nzChartLayerGroup) {
        updateNzChartLayers(map).catch(console.error);
    }
    updateUrlFromMapState(map, baseLayers, overlays, currentFilter, urlState.unit, urlState.level, urlState.mode, urlState.searchPattern, urlState.passagePlan);
});

mapFilterInput.addEventListener('input', () => {
    clearTimeout(filterInputDebounceTimer);
    filterInputDebounceTimer = setTimeout(reloadActiveOverlays, 2000);
});

// Listen for search pattern updates to show/hide the layers control
map.getContainer().addEventListener('search-pattern-updated', updateChartLayersControlVisibility);

// Initial check if the default layer is NZ Charts
if (map.hasLayer(nzChartLayerGroup)) {
    updateNzChartLayers(map).catch(console.error);
}

// Initialize UI Panels
initSettingsPanel(map, baseLayers, overlays, mapFilterInput, () => urlState);
initCoordsPanel(map);
addChartLayersControl(map);
initChartLayersPanel(map);
initIntroPanel(map);
initCoastguardPanel(map);

// Initialize Planners
initPassagePlanner(
    map,
    measureControl,
    urlState.passagePlan ? encodeObjectForUrl(urlState.passagePlan) : undefined,
    (encodedPlan) => {
        urlState.passagePlan = decodeObjectFromUrl(encodedPlan);
        updateUrlFromMapState(map, baseLayers, overlays, mapFilterInput.value, urlState.unit, urlState.level, urlState.mode, urlState.searchPattern, urlState.passagePlan);
    }
);

initPspPlanner(
    map,
    urlState.searchPattern,
    (searchPattern) => {
        urlState.searchPattern = searchPattern;
        updateUrlFromMapState(map, baseLayers, overlays, mapFilterInput.value, urlState.unit, urlState.level, urlState.mode, urlState.searchPattern, urlState.passagePlan);
    }
);

// Initialize Training Mode Panel if active
if (urlState.unit || urlState.level) {
    initTrainingPanel(map, baseLayers, overlays, mapFilterInput);
}

// Initial visibility checks
updateFilterInputVisibility();
updateChartLayersControlVisibility();

// Trigger initial data load.
reloadActiveOverlays();
